import { useEffect, useRef } from "react"
import { Link, useLocation, useNavigate, useOutletContext, useSearchParams } from "react-router-dom"
import AppLayout from "layouts/app-layout"
import moment from "moment/moment"
import Delete from "pages/satuan/delete"
import Filter from "pages/satuan/filter"
import nProgress from "nprogress"
import { useAuth } from "contexts/auth-context"
import { Pagination } from "components"
import { lang } from "config"
import { Pencil, Plus } from "components/icons/outline"
import { ChevronUpDown } from "components/icons/dynamic"
import { usePage } from "contexts/page-context"
import useSatuanRepositories from "repositories/satuan"
import useDebouncedSearchParam from "hooks/use-debounced-search-param"

export default function List() {
    const { can } = useAuth()
    const { configuration } = usePage()

    const { title, transition, setTransition } = useOutletContext()

    const navigate = useNavigate()
    const location = useLocation()

    const [searchParams, setSearchParams] = useSearchParams()
    const searchInput = useRef()

    const page = searchParams.get('page')
    const search = searchParams.get('search')
    const [searchTemp, setSearchTemp] = useDebouncedSearchParam({ search, searchParams, setSearchParams })
    const name = searchParams.get('name')
    const isBase = searchParams.get('is_base')
    const from = searchParams.get('from')
    const to = searchParams.get('to')

    const orderBy = searchParams.get('order_by')
    const direction = searchParams.get('direction')

    const { data: satuanData, isLoading, mutate } = useSatuanRepositories({
        page,
        search,
        name,
        is_base: isBase,
        from,
        to,
        with_base: 1,
        order_by: orderBy,
        direction,
    })

    useEffect(() => {
        window.history.replaceState({}, document.title)

        if (transition.reverse) {
            setTransition({ type: 'slide', reverse: false })
        }
    }, [])

    useEffect(() => {
        searchInput.current?.focus()
    }, [search])

    useEffect(() => {
        if (search === "") {
            navigate(``, { replace: true })
        }

        if (isLoading) {
            nProgress.start()
        } else {
            nProgress.done()
        }
    }, [satuanData, isLoading])

    const updateFilter = (value) => setSearchParams(value)
    const removeFilter = () => setSearchParams({})

    const toggleSort = (value) => {
        if ((direction === null || direction === 'desc') || orderBy !== value) {
            setSearchParams({ order_by: value, direction: 'asc' })
        } else {
            setSearchParams({ order_by: value, direction: 'desc' })
        }
    }

    const formatQty = (qty) => {
        const n = Number(qty)
        if (!isFinite(n)) return qty
        return Number.isInteger(n) ? n.toString() : n.toString().replace(/\.?0+$/, '')
    }

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between text-xs">
                <div className="flex items-center sm:space-x-2">
                    <div className="relative hidden sm:block">
                        <div className="absolute inset-y-0 flex items-center pl-3 pointer-events-none">
                            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" width={24} height={24} viewBox="0 0 24 24" strokeWidth={1} stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                                <circle cx={10} cy={10} r={7}></circle>
                                <line x1={21} y1={21} x2={15} y2={15}></line>
                            </svg>
                        </div>
                        <input ref={searchInput} onChange={(e) => setSearchTemp(e.target.value)} value={searchTemp} type="text" placeholder={`${lang.search} Satuan`} autoComplete="off" className="w-64 py-3 pl-8 pr-4 text-xs transition border border-neutral-200 focus:outline-none rounded-xl focus:border-neutral-400 focus:ring focus:ring-neutral-200" />
                    </div>
                    <Filter
                        onSubmit={updateFilter}
                        onRemove={removeFilter}
                        data={Object.fromEntries(Object.entries({ name, is_base: isBase, from, to }).filter(([_, v]) => v != null))}
                    />
                </div>
                <div className="flex items-center space-x-2">
                    {can('satuan.create') && (
                        <div>
                            <Link to="create" onClick={() => { setTransition({ type: 'slide', reverse: true }) }} state={{ back: title, from: location?.pathname, transition: { type: 'slide' } }} className="inline-flex items-center px-4 py-3 space-x-2 text-white transition bg-neutral-800 rounded-xl active:hover:scale-90">
                                <Plus className="w-4 h-4" stroke={1.5} />
                                <span>{lang.create}</span>
                            </Link>
                        </div>
                    )}
                </div>
            </div>
            <div className="overflow-x-auto border border-neutral-200 rounded-xl">
                <table className="min-w-full overflow-x-auto divide-y divide-neutral-200">
                    <thead className="bg-neutral-50 rounded-t-3xl">
                        <tr>
                            <th scope="col" className="px-6 py-3 whitespace-nowrap">
                                <button className="flex items-center space-x-1 text-xs font-medium text-left uppercase text-neutral-500" onClick={() => toggleSort('name')}>
                                    <span>Nama</span>
                                    <span><ChevronUpDown direction={orderBy === 'name' ? (direction === 'asc' ? 'up' : 'down') : false} className="w-4 h-4" strokeWidth={2} /></span>
                                </button>
                            </th>
                            <th scope="col" className="px-6 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">Pacuan</th>
                            <th scope="col" className="px-6 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">Konversi</th>
                            <th scope="col" className="px-6 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">Tipe</th>
                            <th scope="col" className="px-6 py-3 whitespace-nowrap">
                                <button className="flex items-center space-x-1 text-xs font-medium text-left uppercase text-neutral-500" onClick={() => toggleSort('created_at')}>
                                    <span>{lang.created_at}</span>
                                    <span><ChevronUpDown direction={orderBy === 'created_at' ? (direction === 'asc' ? 'up' : 'down') : false} className="w-4 h-4" strokeWidth={2} /></span>
                                </button>
                            </th>
                            <th scope="col" className="relative px-6 py-3"><span className="sr-only">{lang.action}</span></th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-neutral-200">
                        {isLoading && (
                            <tr className="text-center">
                                <td colSpan="6" className="px-6 py-4 text-xs text-neutral-500 whitespace-nowrap">
                                    {lang.loading_data}
                                </td>
                            </tr>
                        )}

                        {satuanData?.data.length === 0 && !search && !isLoading && (
                            <tr className="text-center">
                                <td colSpan="6" className="px-6 py-4 text-xs text-neutral-500 whitespace-nowrap">
                                    {lang.no_data}
                                </td>
                            </tr>
                        )}

                        {satuanData?.data.length === 0 && search && !isLoading && (
                            <tr className="text-center">
                                <td colSpan="6" className="px-6 py-4 text-xs text-neutral-500 whitespace-nowrap">
                                    {lang.no_result}
                                </td>
                            </tr>
                        )}

                        {satuanData?.data.length > 0 && satuanData.data.map((row) => {
                            const baseName = row.is_base ? row.name : (row.base_satuan?.name ?? '-')
                            return (
                                <tr key={row.id}>
                                    <td className="px-6 py-4 text-xs font-medium text-neutral-900 whitespace-nowrap">
                                        {row.name}
                                        {row.description && (
                                            <div className="mt-1 text-[0.65rem] font-normal text-neutral-500">{row.description}</div>
                                        )}
                                    </td>
                                    <td className="px-6 py-4 text-xs text-neutral-700 whitespace-nowrap">
                                        {row.is_base ? <span className="text-neutral-400 italic">— (dasar)</span> : baseName}
                                    </td>
                                    <td className="px-6 py-4 text-xs text-neutral-700 whitespace-nowrap">
                                        {row.is_base
                                            ? <span>1 {row.name}</span>
                                            : <span>1 {row.name} = {formatQty(row.qty_per_unit)} {baseName}</span>}
                                    </td>
                                    <td className="px-6 py-4 text-xs whitespace-nowrap">
                                        {row.is_base
                                            ? <span className="inline-flex px-2 py-1 text-[0.65rem] font-medium rounded-full bg-emerald-100 text-emerald-700">Dasar</span>
                                            : <span className="inline-flex px-2 py-1 text-[0.65rem] font-medium rounded-full bg-neutral-100 text-neutral-700">Turunan</span>}
                                    </td>
                                    <td className="px-6 py-4 text-xs text-neutral-500 whitespace-nowrap">
                                        {moment(row.created_at).format('MMMM D, YYYY')}
                                    </td>
                                    <td className="px-6 py-4 text-xs font-medium text-right whitespace-nowrap">
                                        <div className="inline-flex items-center space-x-2">
                                            {can('satuan.update') && (
                                                <Link to={`${row.id}/edit`} state={{ back: title, from: location?.pathname, transition: 'slide' }} className="inline-flex items-center p-1 text-white transition rounded-full bg-neutral-800 active:hover:scale-90">
                                                    <Pencil className="w-6 h-6" />
                                                </Link>
                                            )}

                                            {can('satuan.delete') && (
                                                <Delete data={row} onSuccess={() => mutate()} />
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            )
                        })}
                    </tbody>
                </table>
            </div>
            <Pagination links={satuanData?.links} from={satuanData?.from} to={satuanData?.to} total={satuanData?.total} />
        </div>
    )
}
